import React, { useState, useEffect, useRef } from 'react';
import { shareApi } from '../utils/api';
import ErrorMessage from './ErrorMessage';

const ShareModal = ({ 
    isOpen, 
    onClose, 
    shareType, // 'session' hoặc 'message'
    targetId, // session_id hoặc message_id
    defaultTitle = ''
}) => {
    const [title, setTitle] = useState(defaultTitle);
    const [description, setDescription] = useState('');
    const [expiresHours, setExpiresHours] = useState('');
    const [shareUrl, setShareUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [autoCreated, setAutoCreated] = useState(false);
    const creationInitiated = useRef(false);

    // Auto-create share when modal opens
    useEffect(() => {
        // Prevent API call if it has already been initiated
        if (isOpen && !shareUrl && !creationInitiated.current && targetId) {
            creationInitiated.current = true; // Mark as initiated
            createShareWithDefaults();
        }
    }, [isOpen, targetId, shareUrl]);

    const createShareWithDefaults = async () => {
        // Prevent double calls
        if (isLoading) {
            console.log('Share creation already in progress, skipping...');
            return;
        }
        
        setIsLoading(true);
        setError('');

        try {
            // Use values from state (có thể là giá trị mặc định hoặc do user nhập)
            const options = {};
            if (title) options.title = title;
            if (description) options.description = description;
            if (expiresHours) options.expires_hours = parseInt(expiresHours);

            let result;
            if (shareType === 'session') {
                result = await shareApi.shareSession(targetId, options);
            } else if (shareType === 'message') {
                result = await shareApi.shareMessage(targetId, options);
            }

            const fullUrl = `${window.location.origin}${result.share_url}`;
            setShareUrl(fullUrl);
            
            // Update title if it was auto-generated
            if (result.title && !title) {
                setTitle(result.title);
            }
        } catch (err) {
            console.error('Error creating share:', err);
            setError(err.message || 'Có lỗi xảy ra khi tạo chia sẻ');
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = async () => {
        if (shareUrl) {
            await performCopy();
        }
    };

    const performCopy = async (urlToCopy = shareUrl) => {
        try {
            await navigator.clipboard.writeText(urlToCopy);
            // Hiệu ứng flash khi copy thành công
            const button = document.getElementById('copy-button');
            if (button) {
                button.textContent = 'Đã copy!';
                setTimeout(() => {
                    button.textContent = shareUrl ? 'Copy' : 'Tạo link và copy';
                }, 2000);
            }
        } catch (err) {
            console.error('Failed to copy:', err);
            // Fallback cho browsers không hỗ trợ clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = urlToCopy;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }
    };

    const resetModal = () => {
        setTitle(defaultTitle);
        setDescription('');
        setExpiresHours('');
        setShareUrl('');
        setError('');
        setAutoCreated(false);
        creationInitiated.current = false;
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6 min-h-screen">
            <div className="bg-app-bg rounded-lg shadow-xl w-full max-w-md max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Chia sẻ {shareType === 'session' ? 'cuộc trò chuyện' : 'tin nhắn'}
                        </h3>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="space-y-4">
                        {/* Ẩn các field input - chỉ sử dụng giá trị mặc định */}

                        {/* Link chia sẻ */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Link chia sẻ:
                            </label>
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    value={shareUrl || (isLoading ? 'Đang tạo...' : '')}
                                    readOnly
                                    className="flex-1 px-3 py-2 border border-app-border rounded-md bg-app-sidebar text-sm"
                                    placeholder={isLoading ? 'Đang tạo link chia sẻ...' : 'Link sẽ xuất hiện ở đây'}
                                />
                            </div>
                        </div>

                        <ErrorMessage message={error} />

                        <div className="flex space-x-3 pt-4">
                            <button
                                id="copy-button"
                                onClick={copyToClipboard}
                                disabled={isLoading || !shareUrl}
                                className="flex-1 px-4 py-2 bg-brand-blue-500 text-white rounded-md hover:bg-brand-blue-600 disabled:bg-brand-blue-400 disabled:cursor-not-allowed transition-colors"
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        Đang tạo...
                                    </div>
                                ) : (
                                    'Copy link'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
